/**
 * Final Order — Status tab, step 6 (s6)
 *
 * Two top-level flows (status pills):
 *   1. Registered (5A)            → backend filters apps with status="Registered"
 *   2. Refused or Abandoned (5B)  → backend filters apps with status="Refused"|"Abandoned"
 *
 * Each flow has three sub-steps (left rail):
 *   - Status details                (form: fields + file upload — Save & Continue)
 *   - Notice to Client              (forward Order PDFs to WhatsApp — Send/Resend)
 *   - Inform Client                 (send registration / refusal message to WhatsApp — Send/Resend)
 *
 * TM pills are populated from the API for the active status only; an empty list
 * means no applications have reached that final order yet.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getWorkflowStatus } from "api/applications";
import {
  uploadAcknowledgementPdf,
  getRegistrationDetailsStatus,
  setRegistrationDetails,
  forwardRegistrationOrderPdfs,
  getRegistrationMessagePreview,
  sendRegistrationMessage,
  getRefusalAbandonmentDetailsStatus,
  setRefusalAbandonmentDetails,
  forwardRefusalAbandonmentOrderPdfs,
  getRefusalAbandonmentMessagePreview,
  sendRefusalAbandonmentMessage,
  type RegistrationDetailsApplication,
  type RegistrationDetailsStatusResponse,
  type RefusalAbandonmentDetailsApplication,
  type RefusalAbandonmentDetailsStatusResponse,
  type RefusalOrAbandonmentOption,
  type RefusalAbandonmentReasonValue,
  type RefusalAbandonmentMessageGroup,
} from "api/discussion";
import { getLeadDetails } from "api/discussion";
import DateTimePicker from "components/DateTimePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import ConfirmDialog from "components/ConfirmDialog";
import pdfIcon from "assets/svg/pdf-icon.svg";
import radioCheck from "assets/svg/radio-check.svg";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import { toLocalYmd } from "utils/filingApiDates";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type FinalOrderStatus = "registered" | "refused-or-abandoned";
type FinalOrderSubStep = "status-details" | "notice-to-client" | "inform-client";

const REGISTERED_SUB_STEPS: { id: FinalOrderSubStep; label: string }[] = [
  { id: "status-details", label: "Status details of Registration" },
  { id: "notice-to-client", label: "Notice to Client of Registration" },
  { id: "inform-client", label: "Inform Client about Registration" },
];

// Review item 88 — the Refusal/Abandonment rail carried the Registered branch's
// labels verbatim. The review flagged 5B(iii); 5B(ii) was wrong in the same way.
const REFUSED_SUB_STEPS: { id: FinalOrderSubStep; label: string }[] = [
  { id: "status-details", label: "Status details for Refusal or Abandonment" },
  { id: "notice-to-client", label: "Notice to Client for Refusal or Abandonment" },
  { id: "inform-client", label: "Inform Client about Refusal or Abandonment" },
];

const REFUSAL_REASON_OPTIONS: { value: RefusalAbandonmentReasonValue; label: string }[] = [
  {
    value: "FC_FAIL_NON_FILING_OF_REPLY",
    label: "Non Filing of Reply (after Formalities Check Fail)",
  },
  {
    value: "FC_FAIL_NON_APPEARANCE_IN_HEARING",
    label: "Non-appearance in hearing (after Formalities Check Fail)",
  },
  {
    value: "FC_FAIL_DISALLOWANCE_AFTER_HEARING",
    label: "Disallowance after hearing (after Formalities Check Fail)",
  },
  {
    value: "OBJECTION_NON_FILING_OF_REPLY",
    label: "Non Filing of Reply to Objection",
  },
  {
    value: "OBJECTION_NON_APPEARANCE_IN_HEARING",
    label: "Non-appearance in hearing (after Objection)",
  },
  {
    value: "OBJECTION_DISALLOWANCE_AFTER_HEARING",
    label: "Disallowance after hearing (after Objection)",
  },
  {
    value: "OPPOSITION_NON_FILING_OF_COUNTER",
    label: "Non Filing of Counter (Opposition)",
  },
  {
    value: "OPPOSITION_COUNTER_NON_APPEARANCE_IN_HEARING",
    label: "Non-appearance in hearing (after Non Filing of Counter)",
  },
  {
    value: "OPPOSITION_COUNTER_DISALLOWANCE_AFTER_HEARING",
    label: "Disallowance after hearing (after Non Filing of Counter)",
  },
  {
    value: "OPPOSITION_NON_FILING_OF_EVIDENCE",
    label: "Non Filing of Evidence (Opposition)",
  },
  {
    value: "OPPOSITION_EVIDENCE_NON_APPEARANCE_IN_HEARING",
    label: "Non-appearance in hearing (after Non Filing of Evidence)",
  },
  {
    value: "OPPOSITION_EVIDENCE_DISALLOWANCE_AFTER_HEARING",
    label: "Disallowance after hearing (after Non Filing of Evidence)",
  },
  {
    value: "OPPOSITION_HEARING_NON_APPEARANCE",
    label: "Non-appearance in Opposition hearing",
  },
  {
    value: "OPPOSITION_HEARING_DISALLOWANCE",
    label: "Disallowance after Opposition hearing",
  },
];

// Form IDs the dashboard footer targets via `form="…"` attribute
export const FINAL_ORDER_REGISTERED_STATUS_FORM_ID =
  "final-order-registered-status-details-form";
export const FINAL_ORDER_REGISTERED_NOTICE_FORM_ID =
  "final-order-registered-notice-form";
export const FINAL_ORDER_REGISTERED_INFORM_FORM_ID =
  "final-order-registered-inform-form";
export const FINAL_ORDER_REFUSED_STATUS_FORM_ID =
  "final-order-refused-status-details-form";
export const FINAL_ORDER_REFUSED_NOTICE_FORM_ID =
  "final-order-refused-notice-form";
export const FINAL_ORDER_REFUSED_INFORM_FORM_ID =
  "final-order-refused-inform-form";

const REGISTERED_NOTICE_STEP_SLUG = "S3_05A_II_NOTICE_TO_CLIENT_FOR_REGISTRATION";
const REGISTERED_INFORM_STEP_SLUG = "S3_05A_III_INFORM_CLIENT_ABOUT_REGISTRATION";
const REFUSED_NOTICE_STEP_SLUG = "S3_05B_II_NOTICE_TO_CLIENT_REFUSAL_ABANDONMENT";
const REFUSED_INFORM_STEP_SLUG = "S3_05B_III_INFORM_CLIENT_REFUSAL_ABANDONMENT";

const ALLOWED_FILE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "pdf"] as const;
const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;
const ALLOWED_FILE_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";
const SUPPORTED_FORMATS_LABEL =
  "JPG, JPEG, PNG, WEBP, and PDF formats, up to 50MB";
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type DateParts = { year: number; month: number; day: number };

function isAllDigitsStr(s: string): boolean {
  if (!s || s.length === 0) return false;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 48 || c > 57) return false;
  }
  return true;
}

/** Parse DD-MM-YYYY (API format) or YYYY-MM-DD into plain parts. */
function parseDateParts(s: string | null | undefined): DateParts | null {
  if (s == null) return null;
  const t = String(s).trim();
  if (!t) return null;
  const parts = t.split("-");
  if (parts.length !== 3) return null;
  if (
    !isAllDigitsStr(parts[0]) ||
    !isAllDigitsStr(parts[1]) ||
    !isAllDigitsStr(parts[2])
  ) {
    return null;
  }
  let year: number;
  let month: number;
  let day: number;
  if (parts[0].length === 4) {
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[2], 10);
  } else if (parts[2].length === 4) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
    return null;
  }
  return { year, month, day };
}

function partsToDate(p: DateParts): Date {
  return new Date(p.year, p.month - 1, p.day);
}

function ymdFromApi(s: string | null | undefined): string {
  const p = parseDateParts(s);
  if (!p) return "";
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

function formatDisplayDate(s: string | null | undefined): string {
  const p = parseDateParts(s);
  if (!p) return "—";
  return partsToDate(p).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function validateUploadFile(file: File): string | null {
  if (!file) return "No file selected.";
  if (file.size === 0) return "File is empty.";
  if (file.size > MAX_UPLOAD_BYTES) {
    return `File is larger than 50MB (got ${(file.size / (1024 * 1024)).toFixed(1)}MB).`;
  }
  const ext = (file.name.split(".").pop() ?? "").toLowerCase();
  const mime = (file.type || "").toLowerCase();
  const okByExt = (ALLOWED_FILE_EXTENSIONS as readonly string[]).includes(ext);
  const okByMime =
    !mime || (ALLOWED_MIME_TYPES as readonly string[]).includes(mime);
  if (!okByExt || !okByMime) {
    return "Unsupported file type. Allowed: JPG, JPEG, PNG, WEBP, PDF.";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Small visual primitives
// ---------------------------------------------------------------------------

function RadioCircle({ active }: { active: boolean }) {
  if (active) {
    return (
      <img
        src={radioCheck}
        alt=""
        width={14}
        height={14}
        className="flex-shrink-0"
      />
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <circle cx="7" cy="7" r="6.5" stroke="#6D6D6D" />
    </svg>
  );
}

function ChevronRight({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <path
        d="M6 4L10 8L6 12"
        stroke={active ? "white" : "#222222"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RequiredMark() {
  return (
    <span className="text-[#E5252A] ml-0.5" aria-hidden="true">
      *
    </span>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label
      className="text-xs font-normal text-[#222222] leading-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {children}
      {required && <RequiredMark />}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  readOnly,
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`w-full h-9 px-2.5 py-2 text-xs font-normal text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] ${
        readOnly ? "bg-[#FCFCFC] text-[#6D6D6D] cursor-not-allowed" : ""
      }`}
      style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
    />
  );
}

function ReadOnlyDateBox({ value }: { value: string }) {
  return (
    <div
      className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-[#FCFCFC] flex items-center"
      style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
    >
      {value || "—"}
    </div>
  );
}

// ---------------------------------------------------------------------------
// File upload zone — single TM upload box matching AdvertisedInJournal pattern
// ---------------------------------------------------------------------------

type PendingFile = File | null;

function FileUploadZone({
  inputIdPrefix,
  tmNo,
  pendingFile,
  serverFilename,
  hasServerFile,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onPick,
  onPreview,
  onRemove,
}: {
  inputIdPrefix: string;
  tmNo: string;
  pendingFile: PendingFile;
  serverFilename: string | null;
  hasServerFile: boolean;
  isDragging: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (file: File) => void;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPreview: () => void;
  onRemove: () => void;
}) {
  const inputId = `${inputIdPrefix}-${tmNo}`;
  const hasFile = !!(pendingFile || hasServerFile);
  const fileName = pendingFile?.name ?? serverFilename ?? "File selected";

  return (
    <div className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 md:p-5">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-[#101111]">
          File Upload
          <RequiredMark />
        </p>
        <p className="text-xs text-[#7D7D7D]">
          Choose a file and upload securely to proceed.
        </p>
      </div>

      <div
        className={`flex flex-col md:flex-row items-stretch md:items-center gap-4 rounded-2xl border border-dashed px-4 py-4 cursor-pointer transition-colors ${
          isDragging
            ? "border-[#222222] bg-[#F5F5F5]"
            : hasFile
              ? "border-[#22C55E] bg-[#F0FDF4]"
              : "border-[#D8DAEB] bg-white"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver();
        }}
        onDragLeave={() => onDragLeave()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) onDrop(file);
        }}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
          <img
            src={uploadDropzoneGraphic}
            alt=""
            width={60}
            height={60}
            className="flex-shrink-0"
          />
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            {hasFile ? (
              <p className="text-sm font-medium text-[#16A34A] truncate">
                {fileName}
              </p>
            ) : (
              <>
                <p className="text-base font-medium text-[#101111]">
                  Drag and drop your files
                </p>
                <p className="text-sm text-[#7D7D7D]">
                  {SUPPORTED_FORMATS_LABEL}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 flex-shrink-0 self-end md:self-center">
          {!hasFile && (
            <button
              type="button"
              className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-sm font-medium text-[#2E3037] hover:bg-[#F5F5F5] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById(inputId)?.click();
              }}
            >
              Select File
            </button>
          )}
          {hasFile && (
            <>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview();
                }}
              >
                Preview
              </button>
              <button
                type="button"
                className="px-4 h-9 rounded-[10px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#6D6D6D] hover:bg-[#F5F5F5]"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>

      <input
        id={inputId}
        type="file"
        accept={ALLOWED_FILE_ACCEPT}
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF row used in Notice to Client list
// ---------------------------------------------------------------------------

function NoticePdfRow({
  label,
  onPreview,
  disabled,
}: {
  label: string;
  onPreview: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="flex flex-row items-center gap-3 px-4 py-1 rounded-[6px] bg-[#FCFCFC] border border-[#EEEEEE] h-11"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img src={pdfIcon} alt="" width={22} height={22} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-[#222222] truncate block">
          {label}
        </span>
      </div>
      <button
        type="button"
        onClick={onPreview}
        disabled={disabled}
        className="px-4 h-9 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333333] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Preview
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export type FinalOrderViewProps = {
  leadId?: string | null;
  onSavingChange?: (saving: boolean) => void;
  onSuccess?: (leadId: string) => void;

  // Registered footer states
  onRegisteredStatusFooterFormModeChange?: (needsSubmit: boolean) => void;
  onRegisteredNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  onRegisteredNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  onRegisteredInformFooterSendModeChange?: (needsSend: boolean) => void;
  onRegisteredInformFooterSaveModeChange?: (needsSave: boolean) => void;

  // Refused/Abandoned footer states
  onRefusedStatusFooterFormModeChange?: (needsSubmit: boolean) => void;
  onRefusedNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  onRefusedNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  onRefusedInformFooterSendModeChange?: (needsSend: boolean) => void;
  onRefusedInformFooterSaveModeChange?: (needsSave: boolean) => void;
};

type RegisteredFormState = {
  dateOfIssue: string;
  certificateNo: string;
  pendingFile: PendingFile;
};

type RefusedFormState = {
  refusalOrAbandonment: RefusalOrAbandonmentOption | "";
  dateOfOrder: string;
  refusalReason: RefusalAbandonmentReasonValue | "";
  pendingFile: PendingFile;
};

type ConfirmAction =
  | "send-registered-notice"
  | "resend-registered-notice"
  | "send-registered-inform"
  | "resend-registered-inform"
  | "send-refused-notice"
  | "resend-refused-notice"
  | "send-refused-inform"
  | "resend-refused-inform";

const CONFIRM_ACTION_COPY: Record<
  ConfirmAction,
  { title: string; description: string; confirmLabel: string }
> = {
  "send-registered-notice": {
    title: "Send Order of Registration?",
    description:
      "The Order of Registration PDF(s) will be forwarded to the client on WhatsApp.",
    confirmLabel: "Send",
  },
  "resend-registered-notice": {
    title: "Resend Order of Registration?",
    description:
      "The Order of Registration PDF(s) will be resent to the client on WhatsApp.",
    confirmLabel: "Resend",
  },
  "send-registered-inform": {
    title: "Send Registration Details?",
    description:
      "The Registration details message will be sent to the client on WhatsApp.",
    confirmLabel: "Send",
  },
  "resend-registered-inform": {
    title: "Resend Registration Details?",
    description:
      "The Registration details message will be resent to the client on WhatsApp.",
    confirmLabel: "Resend",
  },
  "send-refused-notice": {
    title: "Send Order of Refusal/Abandonment?",
    description:
      "The Order of Refusal/Abandonment PDF(s) will be forwarded to the client on WhatsApp.",
    confirmLabel: "Send",
  },
  "resend-refused-notice": {
    title: "Resend Order of Refusal/Abandonment?",
    description:
      "The Order of Refusal/Abandonment PDF(s) will be resent to the client on WhatsApp.",
    confirmLabel: "Resend",
  },
  "send-refused-inform": {
    title: "Send Refusal/Abandonment Details?",
    description:
      "The Refusal/Abandonment details message will be sent to the client on WhatsApp.",
    confirmLabel: "Send",
  },
  "resend-refused-inform": {
    title: "Resend Refusal/Abandonment Details?",
    description:
      "The Refusal/Abandonment details message will be resent to the client on WhatsApp.",
    confirmLabel: "Resend",
  },
};

function registeredAppToForm(
  app: RegistrationDetailsApplication
): RegisteredFormState {
  return {
    dateOfIssue: ymdFromApi(app.dateOfIssue),
    certificateNo: app.certificateNo ?? "",
    pendingFile: null,
  };
}

function refusedAppToForm(
  app: RefusalAbandonmentDetailsApplication
): RefusedFormState {
  return {
    refusalOrAbandonment:
      (app.refusalOrAbandonment as RefusalOrAbandonmentOption | null) ?? "",
    dateOfOrder: ymdFromApi(app.dateOfOrder),
    refusalReason: app.refusalReason ?? "",
    pendingFile: null,
  };
}

export default function FinalOrderView({
  leadId,
  onSavingChange,
  onSuccess,
  onRegisteredStatusFooterFormModeChange,
  onRegisteredNoticeFooterSendModeChange,
  onRegisteredNoticeFooterSaveModeChange,
  onRegisteredInformFooterSendModeChange,
  onRegisteredInformFooterSaveModeChange,
  onRefusedStatusFooterFormModeChange,
  onRefusedNoticeFooterSendModeChange,
  onRefusedNoticeFooterSaveModeChange,
  onRefusedInformFooterSendModeChange,
  onRefusedInformFooterSaveModeChange,
}: FinalOrderViewProps) {
  const [searchParams] = useSearchParams();
  const initialFinalOrderStatus: FinalOrderStatus =
    searchParams.get("sub") === "refused-or-abandoned"
      ? "refused-or-abandoned"
      : "registered";
  const [finalOrderStatus, setFinalOrderStatus] =
    useState<FinalOrderStatus>(initialFinalOrderStatus);
  const [subStep, setSubStep] = useState<FinalOrderSubStep>("status-details");
  const [loading, setLoading] = useState(!!leadId);
  const [phone, setPhone] = useState<string>("");

  // Registered state
  const [registeredStatus, setRegisteredStatus] =
    useState<RegistrationDetailsStatusResponse | null>(null);
  const [registeredForms, setRegisteredForms] = useState<
    Record<string, RegisteredFormState>
  >({});
  const [selectedRegisteredTmNo, setSelectedRegisteredTmNo] = useState<string>("");
  const [registeredMessage, setRegisteredMessage] = useState<string | null>(null);
  const [registeredMessageLoading, setRegisteredMessageLoading] =
    useState(false);
  const [hasSentRegisteredNotice, setHasSentRegisteredNotice] = useState(false);
  const [hasSentRegisteredInform, setHasSentRegisteredInform] = useState(false);
  const [resendingRegisteredNotice, setResendingRegisteredNotice] =
    useState(false);
  const [resendingRegisteredInform, setResendingRegisteredInform] =
    useState(false);

  // Refused/Abandoned state
  const [refusedStatus, setRefusedStatus] =
    useState<RefusalAbandonmentDetailsStatusResponse | null>(null);
  const [refusedForms, setRefusedForms] = useState<
    Record<string, RefusedFormState>
  >({});
  const [selectedRefusedTmNo, setSelectedRefusedTmNo] = useState<string>("");
  // Item 89 — 5B(iii) sends one message per (5f) reason, 5d) outcome) group, so the
  // preview is a list. Applications with different statuses are informed separately.
  const [refusedMessages, setRefusedMessages] = useState<
    RefusalAbandonmentMessageGroup[]
  >([]);
  const [refusedMessageLoading, setRefusedMessageLoading] = useState(false);
  const [hasSentRefusedNotice, setHasSentRefusedNotice] = useState(false);
  const [hasSentRefusedInform, setHasSentRefusedInform] = useState(false);
  const [resendingRefusedNotice, setResendingRefusedNotice] = useState(false);
  const [resendingRefusedInform, setResendingRefusedInform] = useState(false);

  // Shared UI state
  const [dragOverTmNo, setDragOverTmNo] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind?: ReturnType<typeof detectPreviewKind>;
    revokeOnClose?: boolean;
  } | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---------------------------------------------------------------------------
  // Initial data load: phone + both registered and refused statuses + workflow flags
  // ---------------------------------------------------------------------------

  const refreshRegistered = useCallback(async () => {
    if (!leadId) return null;
    const data = await getRegistrationDetailsStatus(leadId);
    setRegisteredStatus(data);
    const map: Record<string, RegisteredFormState> = {};
    for (const app of data.applications) {
      map[app.tmNo] = registeredAppToForm(app);
    }
    setRegisteredForms(map);
    if (data.applications.length > 0) {
      setSelectedRegisteredTmNo((curr) =>
        data.applications.some((a) => a.tmNo === curr)
          ? curr
          : data.applications[0].tmNo
      );
    } else {
      setSelectedRegisteredTmNo("");
    }
    return data;
  }, [leadId]);

  const refreshRefused = useCallback(async () => {
    if (!leadId) return null;
    const data = await getRefusalAbandonmentDetailsStatus(leadId);
    setRefusedStatus(data);
    const map: Record<string, RefusedFormState> = {};
    for (const app of data.applications) {
      map[app.tmNo] = refusedAppToForm(app);
    }
    setRefusedForms(map);
    if (data.applications.length > 0) {
      setSelectedRefusedTmNo((curr) =>
        data.applications.some((a) => a.tmNo === curr)
          ? curr
          : data.applications[0].tmNo
      );
    } else {
      setSelectedRefusedTmNo("");
    }
    return data;
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setPhone("");
      setRegisteredStatus(null);
      setRefusedStatus(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getLeadDetails(leadId)
        .then((d) => d.lead.phone ?? "")
        .catch(() => ""),
      refreshRegistered().catch(() => null),
      refreshRefused().catch(() => null),
      getWorkflowStatus(leadId).catch(() => null),
    ])
      .then(([ph, _reg, _ref, workflow]) => {
        if (cancelled) return;
        setPhone(ph);
        if (workflow) {
          const findStep = (slug: string) =>
            workflow.steps.find((s: { slug: string }) => s.slug === slug);
          setHasSentRegisteredNotice(
            Boolean(findStep(REGISTERED_NOTICE_STEP_SLUG)?.isCompleted)
          );
          setHasSentRegisteredInform(
            Boolean(findStep(REGISTERED_INFORM_STEP_SLUG)?.isCompleted)
          );
          setHasSentRefusedNotice(
            Boolean(findStep(REFUSED_NOTICE_STEP_SLUG)?.isCompleted)
          );
          setHasSentRefusedInform(
            Boolean(findStep(REFUSED_INFORM_STEP_SLUG)?.isCompleted)
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, refreshRegistered, refreshRefused]);

  // ---------------------------------------------------------------------------
  // Message previews — load on entering Inform Client sub-step
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (
      !leadId ||
      finalOrderStatus !== "registered" ||
      subStep !== "inform-client"
    ) {
      return;
    }
    let cancelled = false;
    setRegisteredMessageLoading(true);
    setRegisteredMessage(null);
    getRegistrationMessagePreview(leadId)
      .then(({ message }) => {
        if (!cancelled) setRegisteredMessage(message);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load message preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setRegisteredMessageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, finalOrderStatus, subStep]);

  useEffect(() => {
    if (
      !leadId ||
      finalOrderStatus !== "refused-or-abandoned" ||
      subStep !== "inform-client"
    ) {
      return;
    }
    if (!refusedStatus?.canSendMessage) {
      setRefusedMessages([]);
      return;
    }
    let cancelled = false;
    setRefusedMessageLoading(true);
    setRefusedMessages([]);
    getRefusalAbandonmentMessagePreview(leadId)
      .then(({ messages, message }) => {
        if (cancelled) return;
        // Tolerate an older backend that only returns the flat `message`.
        setRefusedMessages(
          messages?.length
            ? messages
            : message
            ? [
                {
                  message,
                  refusalReason: null,
                  refusalOrAbandonment: null,
                  tmNos: [],
                  applicationCount: 0,
                },
              ]
            : []
        );
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load message preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setRefusedMessageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, finalOrderStatus, subStep, refusedStatus?.canSendMessage]);

  // ---------------------------------------------------------------------------
  // Footer mode signaling
  // ---------------------------------------------------------------------------

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "registered" && subStep === "status-details";
    onRegisteredStatusFooterFormModeChange?.(active);
  }, [finalOrderStatus, subStep, onRegisteredStatusFooterFormModeChange]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "registered" &&
      subStep === "notice-to-client" &&
      !hasSentRegisteredNotice;
    onRegisteredNoticeFooterSendModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRegisteredNotice,
    onRegisteredNoticeFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "registered" &&
      subStep === "notice-to-client" &&
      hasSentRegisteredNotice;
    onRegisteredNoticeFooterSaveModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRegisteredNotice,
    onRegisteredNoticeFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "registered" &&
      subStep === "inform-client" &&
      !hasSentRegisteredInform;
    onRegisteredInformFooterSendModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRegisteredInform,
    onRegisteredInformFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "registered" &&
      subStep === "inform-client" &&
      hasSentRegisteredInform;
    onRegisteredInformFooterSaveModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRegisteredInform,
    onRegisteredInformFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "refused-or-abandoned" && subStep === "status-details";
    onRefusedStatusFooterFormModeChange?.(active);
  }, [finalOrderStatus, subStep, onRefusedStatusFooterFormModeChange]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "refused-or-abandoned" &&
      subStep === "notice-to-client" &&
      !hasSentRefusedNotice;
    onRefusedNoticeFooterSendModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRefusedNotice,
    onRefusedNoticeFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "refused-or-abandoned" &&
      subStep === "notice-to-client" &&
      hasSentRefusedNotice;
    onRefusedNoticeFooterSaveModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRefusedNotice,
    onRefusedNoticeFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "refused-or-abandoned" &&
      subStep === "inform-client" &&
      !hasSentRefusedInform;
    onRefusedInformFooterSendModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRefusedInform,
    onRefusedInformFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    const active =
      finalOrderStatus === "refused-or-abandoned" &&
      subStep === "inform-client" &&
      hasSentRefusedInform;
    onRefusedInformFooterSaveModeChange?.(active);
  }, [
    finalOrderStatus,
    subStep,
    hasSentRefusedInform,
    onRefusedInformFooterSaveModeChange,
  ]);

  // Reset sub-step when switching status
  const handleStatusSwitch = (next: FinalOrderStatus) => {
    if (next === finalOrderStatus) return;
    setFinalOrderStatus(next);
    setSubStep("status-details");
  };

  // ---------------------------------------------------------------------------
  // Registered: form field updates (with optional auto-save on date change)
  // ---------------------------------------------------------------------------

  const updateRegisteredForm = (
    tmNo: string,
    patch: Partial<RegisteredFormState>
  ) => {
    setRegisteredForms((prev) => {
      const base = prev[tmNo] ?? {
        dateOfIssue: "",
        certificateNo: "",
        pendingFile: null,
      };
      return { ...prev, [tmNo]: { ...base, ...patch } };
    });
  };

  const persistRegistered = useCallback(
    async (
      tmNo: string,
      overrides: Partial<RegisteredFormState>
    ): Promise<RegistrationDetailsStatusResponse | null> => {
      if (!leadId) return null;
      const current = registeredForms[tmNo] ?? {
        dateOfIssue: "",
        certificateNo: "",
        pendingFile: null,
      };
      const merged: RegisteredFormState = { ...current, ...overrides };
      // Upload pending file if present
      let fileKey: string | null | undefined;
      let fileName: string | null | undefined;
      if (merged.pendingFile) {
        const file = merged.pendingFile;
        fileKey = await uploadAcknowledgementPdf(
          file,
          `status/registration-details/${leadId}`
        );
        fileName = file.name;
      }
      const updated = await setRegistrationDetails(leadId, {
        applications: [
          {
            tmNo,
            dateOfIssue: merged.dateOfIssue || null,
            certificateNo: merged.certificateNo || null,
            ...(fileKey
              ? {
                  orderOfRegistrationFileKey: fileKey,
                  orderOfRegistrationFilename: fileName,
                }
              : {}),
          },
        ],
      });
      setRegisteredStatus(updated);
      setRegisteredForms((prev) => {
        const next = { ...prev };
        for (const app of updated.applications) {
          const existing = next[app.tmNo];
          next[app.tmNo] = {
            dateOfIssue: ymdFromApi(app.dateOfIssue),
            certificateNo: app.certificateNo ?? "",
            pendingFile:
              app.tmNo === tmNo && fileKey ? null : existing?.pendingFile ?? null,
          };
        }
        return next;
      });
      return updated;
    },
    [leadId, registeredForms]
  );

  const handleRegisteredDateOfIssueChange = (tmNo: string, date: Date | null) => {
    const ymd = date ? toLocalYmd(date) : "";
    updateRegisteredForm(tmNo, { dateOfIssue: ymd });
    if (!leadId || !ymd) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void persistRegistered(tmNo, { dateOfIssue: ymd }).catch((err) => {
        toast.error(
          err instanceof Error ? err.message : "Failed to auto-save date"
        );
      });
    }, 800);
  };

  const handleRegisteredCertificateNoChange = (tmNo: string, v: string) => {
    updateRegisteredForm(tmNo, { certificateNo: v });
    if (!leadId) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void persistRegistered(tmNo, { certificateNo: v }).catch((err) => {
        toast.error(
          err instanceof Error ? err.message : "Failed to auto-save"
        );
      });
    }, 800);
  };

  const handleRegisteredFileChosen = (tmNo: string, file: File) => {
    const reason = validateUploadFile(file);
    if (reason) {
      toast.error(reason);
      return;
    }
    updateRegisteredForm(tmNo, { pendingFile: file });
  };

  const handleRegisteredFileInput = (
    tmNo: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) handleRegisteredFileChosen(tmNo, file);
    e.target.value = "";
  };

  const removeRegisteredFile = (tmNo: string) => {
    updateRegisteredForm(tmNo, { pendingFile: null });
    setRegisteredStatus((prev) =>
      prev
        ? {
            ...prev,
            applications: prev.applications.map((a) =>
              a.tmNo === tmNo
                ? {
                    ...a,
                    orderOfRegistrationFileKey: null,
                    orderOfRegistrationFilename: null,
                    orderOfRegistrationFileUrl: null,
                  }
                : a
            ),
          }
        : prev
    );
  };

  const openRegisteredFilePreview = (
    app: RegistrationDetailsApplication
  ) => {
    const form = registeredForms[app.tmNo];
    if (form?.pendingFile) {
      setPreview({
        url: URL.createObjectURL(form.pendingFile),
        name: form.pendingFile.name,
        kind: detectPreviewKind(form.pendingFile.name, form.pendingFile.type),
        revokeOnClose: true,
      });
      return;
    }
    if (app.orderOfRegistrationFileUrl && app.orderOfRegistrationFilename) {
      setPreview({
        url: app.orderOfRegistrationFileUrl,
        name: app.orderOfRegistrationFilename,
        kind: detectPreviewKind(app.orderOfRegistrationFilename),
      });
    } else {
      toast.error("No file available to preview");
    }
  };

  // Registered form submit (Status Details: save & advance)
  const handleRegisteredStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    const selectedApp = registeredStatus?.applications.find(
      (a) => a.tmNo === selectedRegisteredTmNo
    );
    if (!selectedApp) {
      toast.error("Select a TM application first");
      return;
    }
    const form = registeredForms[selectedApp.tmNo];
    if (!form?.dateOfIssue) {
      toast.error(
        `Enter Date of issue for ${formatTmNoForDisplay(selectedApp.tmNo)}`
      );
      return;
    }
    if (!form?.certificateNo?.trim()) {
      toast.error(
        `Enter Certificate No. for ${formatTmNoForDisplay(selectedApp.tmNo)}`
      );
      return;
    }
    const hasFile = Boolean(
      form?.pendingFile || selectedApp.orderOfRegistrationFileKey
    );
    if (!hasFile) {
      toast.error(
        `Upload Order of Registration PDF for ${formatTmNoForDisplay(selectedApp.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      await persistRegistered(selectedApp.tmNo, {});
      toast.success("Registration details saved");
      setSubStep("notice-to-client");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Registration Details"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  // Registered Notice (forward PDFs) submit
  const performRegisteredNoticeSend = async () => {
    if (!leadId || !registeredStatus?.canForwardPdfs) return;
    setConfirmBusy(true);
    onSavingChange?.(true);
    try {
      await forwardRegistrationOrderPdfs(leadId);
      setHasSentRegisteredNotice(true);
      toast.success("Order of Registration PDF(s) sent to client");
      setSubStep("inform-client");
      onSuccess?.(leadId);
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to forward PDFs"
      );
    } finally {
      setConfirmBusy(false);
      onSavingChange?.(false);
    }
  };

  const handleRegisteredNoticeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentRegisteredNotice) {
      setSubStep("inform-client");
      return;
    }
    if (!registeredStatus?.canForwardPdfs) {
      toast.error("Upload Order of Registration PDF in Status Details first.");
      return;
    }
    setConfirmAction("send-registered-notice");
  };

  const performRegisteredNoticeResend = async () => {
    if (
      !leadId ||
      resendingRegisteredNotice ||
      !registeredStatus?.canForwardPdfs
    ) {
      return;
    }
    setConfirmBusy(true);
    setResendingRegisteredNotice(true);
    onSavingChange?.(true);
    try {
      await forwardRegistrationOrderPdfs(leadId);
      toast.success("Order of Registration PDF(s) resent to client");
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to resend PDFs"
      );
    } finally {
      setConfirmBusy(false);
      setResendingRegisteredNotice(false);
      onSavingChange?.(false);
    }
  };

  const handleRegisteredNoticeResend = () => {
    if (
      !leadId ||
      resendingRegisteredNotice ||
      !registeredStatus?.canForwardPdfs
    ) {
      return;
    }
    setConfirmAction("resend-registered-notice");
  };

  // Registered Inform Client submit
  const performRegisteredInformSend = async () => {
    if (!leadId) return;
    setConfirmBusy(true);
    onSavingChange?.(true);
    try {
      await sendRegistrationMessage(leadId);
      setHasSentRegisteredInform(true);
      toast.success("Registration details sent to client");
      onSuccess?.(leadId);
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      setConfirmBusy(false);
      onSavingChange?.(false);
    }
  };

  const handleRegisteredInformSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentRegisteredInform) {
      onSuccess?.(leadId);
      return;
    }
    setConfirmAction("send-registered-inform");
  };

  const performRegisteredInformResend = async () => {
    if (!leadId || resendingRegisteredInform) return;
    setConfirmBusy(true);
    setResendingRegisteredInform(true);
    onSavingChange?.(true);
    try {
      await sendRegistrationMessage(leadId);
      toast.success("Registration message resent to client");
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to resend message"
      );
    } finally {
      setConfirmBusy(false);
      setResendingRegisteredInform(false);
      onSavingChange?.(false);
    }
  };

  const handleRegisteredInformResend = () => {
    if (!leadId || resendingRegisteredInform) return;
    setConfirmAction("resend-registered-inform");
  };

  // ---------------------------------------------------------------------------
  // Refused/Abandoned: form field updates (auto-save on change)
  // ---------------------------------------------------------------------------

  const updateRefusedForm = (
    tmNo: string,
    patch: Partial<RefusedFormState>
  ) => {
    setRefusedForms((prev) => {
      const base = prev[tmNo] ?? {
        refusalOrAbandonment: "",
        dateOfOrder: "",
        refusalReason: "",
        pendingFile: null,
      };
      return { ...prev, [tmNo]: { ...base, ...patch } };
    });
  };

  const persistRefused = useCallback(
    async (
      tmNo: string,
      overrides: Partial<RefusedFormState>
    ): Promise<RefusalAbandonmentDetailsStatusResponse | null> => {
      if (!leadId) return null;
      const current = refusedForms[tmNo] ?? {
        refusalOrAbandonment: "",
        dateOfOrder: "",
        refusalReason: "",
        pendingFile: null,
      };
      const merged: RefusedFormState = { ...current, ...overrides };
      let fileKey: string | null | undefined;
      let fileName: string | null | undefined;
      if (merged.pendingFile) {
        const file = merged.pendingFile;
        fileKey = await uploadAcknowledgementPdf(
          file,
          `status/refusal-abandonment-details/${leadId}`
        );
        fileName = file.name;
      }
      const updated = await setRefusalAbandonmentDetails(leadId, {
        applications: [
          {
            tmNo,
            refusalOrAbandonment:
              (merged.refusalOrAbandonment as RefusalOrAbandonmentOption) || null,
            dateOfOrder: merged.dateOfOrder || null,
            refusalReason:
              (merged.refusalReason as RefusalAbandonmentReasonValue) || null,
            ...(fileKey
              ? {
                  orderFileKey: fileKey,
                  orderFilename: fileName,
                }
              : {}),
          },
        ],
      });
      setRefusedStatus(updated);
      setRefusedForms((prev) => {
        const next = { ...prev };
        for (const app of updated.applications) {
          const existing = next[app.tmNo];
          next[app.tmNo] = {
            refusalOrAbandonment:
              (app.refusalOrAbandonment as RefusalOrAbandonmentOption | null) ?? "",
            dateOfOrder: ymdFromApi(app.dateOfOrder),
            refusalReason: app.refusalReason ?? "",
            pendingFile:
              app.tmNo === tmNo && fileKey ? null : existing?.pendingFile ?? null,
          };
        }
        return next;
      });
      return updated;
    },
    [leadId, refusedForms]
  );

  const scheduleRefusedAutoSave = (
    tmNo: string,
    overrides: Partial<RefusedFormState>
  ) => {
    if (!leadId) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void persistRefused(tmNo, overrides).catch((err) => {
        toast.error(
          err instanceof Error ? err.message : "Failed to auto-save"
        );
      });
    }, 800);
  };

  const handleRefusedOptionChange = (
    tmNo: string,
    value: RefusalOrAbandonmentOption
  ) => {
    updateRefusedForm(tmNo, { refusalOrAbandonment: value });
    scheduleRefusedAutoSave(tmNo, { refusalOrAbandonment: value });
  };

  const handleRefusedDateChange = (tmNo: string, date: Date | null) => {
    const ymd = date ? toLocalYmd(date) : "";
    updateRefusedForm(tmNo, { dateOfOrder: ymd });
    if (ymd) scheduleRefusedAutoSave(tmNo, { dateOfOrder: ymd });
  };

  const handleRefusedReasonChange = (
    tmNo: string,
    value: RefusalAbandonmentReasonValue
  ) => {
    updateRefusedForm(tmNo, { refusalReason: value });
    scheduleRefusedAutoSave(tmNo, { refusalReason: value });
  };

  const handleRefusedFileChosen = (tmNo: string, file: File) => {
    const reason = validateUploadFile(file);
    if (reason) {
      toast.error(reason);
      return;
    }
    updateRefusedForm(tmNo, { pendingFile: file });
  };

  const handleRefusedFileInput = (
    tmNo: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) handleRefusedFileChosen(tmNo, file);
    e.target.value = "";
  };

  const removeRefusedFile = (tmNo: string) => {
    updateRefusedForm(tmNo, { pendingFile: null });
    setRefusedStatus((prev) =>
      prev
        ? {
            ...prev,
            applications: prev.applications.map((a) =>
              a.tmNo === tmNo
                ? {
                    ...a,
                    orderFileKey: null,
                    orderFilename: null,
                    orderFileUrl: null,
                  }
                : a
            ),
          }
        : prev
    );
  };

  const openRefusedFilePreview = (
    app: RefusalAbandonmentDetailsApplication
  ) => {
    const form = refusedForms[app.tmNo];
    if (form?.pendingFile) {
      setPreview({
        url: URL.createObjectURL(form.pendingFile),
        name: form.pendingFile.name,
        kind: detectPreviewKind(form.pendingFile.name, form.pendingFile.type),
        revokeOnClose: true,
      });
      return;
    }
    if (app.orderFileUrl && app.orderFilename) {
      setPreview({
        url: app.orderFileUrl,
        name: app.orderFilename,
        kind: detectPreviewKind(app.orderFilename),
      });
    } else {
      toast.error("No file available to preview");
    }
  };

  const handleRefusedStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    const selectedApp = refusedStatus?.applications.find(
      (a) => a.tmNo === selectedRefusedTmNo
    );
    if (!selectedApp) {
      toast.error("Select a TM application first");
      return;
    }
    const form = refusedForms[selectedApp.tmNo];
    if (!form?.refusalOrAbandonment) {
      toast.error("Pick Refused or Abandoned");
      return;
    }
    if (!form?.dateOfOrder) {
      toast.error("Enter Date of Order of Refusal or Abandonment");
      return;
    }
    if (!form?.refusalReason) {
      toast.error("Select a refusal reason");
      return;
    }
    const hasFile = Boolean(form?.pendingFile || selectedApp.orderFileKey);
    if (!hasFile) {
      toast.error(
        `Upload Order PDF for ${formatTmNoForDisplay(selectedApp.tmNo)}`
      );
      return;
    }
    onSavingChange?.(true);
    try {
      await persistRefused(selectedApp.tmNo, {});
      toast.success("Refusal/Abandonment details saved");
      setSubStep("notice-to-client");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Refusal/Abandonment Details"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const performRefusedNoticeSend = async () => {
    if (!leadId || !refusedStatus?.canForwardPdfs) return;
    setConfirmBusy(true);
    onSavingChange?.(true);
    try {
      await forwardRefusalAbandonmentOrderPdfs(leadId);
      setHasSentRefusedNotice(true);
      toast.success("Order PDF(s) sent to client");
      setSubStep("inform-client");
      onSuccess?.(leadId);
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to forward PDFs"
      );
    } finally {
      setConfirmBusy(false);
      onSavingChange?.(false);
    }
  };

  const handleRefusedNoticeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentRefusedNotice) {
      setSubStep("inform-client");
      return;
    }
    if (!refusedStatus?.canForwardPdfs) {
      toast.error("Upload Order PDF in Status Details first.");
      return;
    }
    setConfirmAction("send-refused-notice");
  };

  const performRefusedNoticeResend = async () => {
    if (!leadId || resendingRefusedNotice || !refusedStatus?.canForwardPdfs) {
      return;
    }
    setConfirmBusy(true);
    setResendingRefusedNotice(true);
    onSavingChange?.(true);
    try {
      await forwardRefusalAbandonmentOrderPdfs(leadId);
      toast.success("Order PDF(s) resent to client");
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to resend PDFs"
      );
    } finally {
      setConfirmBusy(false);
      setResendingRefusedNotice(false);
      onSavingChange?.(false);
    }
  };

  const handleRefusedNoticeResend = () => {
    if (!leadId || resendingRefusedNotice || !refusedStatus?.canForwardPdfs) {
      return;
    }
    setConfirmAction("resend-refused-notice");
  };

  const performRefusedInformSend = async () => {
    if (!leadId || !refusedStatus?.canSendMessage) return;
    setConfirmBusy(true);
    onSavingChange?.(true);
    try {
      const { messageCount } = await sendRefusalAbandonmentMessage(leadId);
      setHasSentRefusedInform(true);
      toast.success(
        messageCount > 1
          ? `Refusal/Abandonment details sent to client (${messageCount} messages)`
          : "Refusal/Abandonment details sent to client"
      );
      onSuccess?.(leadId);
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      setConfirmBusy(false);
      onSavingChange?.(false);
    }
  };

  const handleRefusedInformSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSentRefusedInform) {
      onSuccess?.(leadId);
      return;
    }
    if (!refusedStatus?.canSendMessage) {
      toast.error(
        "Set refusal reason for all applications in Status Details first."
      );
      return;
    }
    setConfirmAction("send-refused-inform");
  };

  const performRefusedInformResend = async () => {
    if (!leadId || resendingRefusedInform) return;
    setConfirmBusy(true);
    setResendingRefusedInform(true);
    onSavingChange?.(true);
    try {
      const { messageCount } = await sendRefusalAbandonmentMessage(leadId);
      toast.success(
        messageCount > 1
          ? `Refusal/Abandonment messages resent to client (${messageCount})`
          : "Refusal/Abandonment message resent to client"
      );
      setConfirmAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to resend message"
      );
    } finally {
      setConfirmBusy(false);
      setResendingRefusedInform(false);
      onSavingChange?.(false);
    }
  };

  const handleRefusedInformResend = () => {
    if (!leadId || resendingRefusedInform) return;
    setConfirmAction("resend-refused-inform");
  };

  // Cleanup autosave timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Derived selections
  // ---------------------------------------------------------------------------

  const subSteps =
    finalOrderStatus === "registered" ? REGISTERED_SUB_STEPS : REFUSED_SUB_STEPS;

  const registeredApps = useMemo(
    () => registeredStatus?.applications ?? [],
    [registeredStatus]
  );
  const refusedApps = useMemo(
    () => refusedStatus?.applications ?? [],
    [refusedStatus]
  );

  const selectedRegisteredApp = useMemo(
    () => registeredApps.find((a) => a.tmNo === selectedRegisteredTmNo) ?? null,
    [registeredApps, selectedRegisteredTmNo]
  );
  const selectedRefusedApp = useMemo(
    () => refusedApps.find((a) => a.tmNo === selectedRefusedTmNo) ?? null,
    [refusedApps, selectedRefusedTmNo]
  );

  const registeredAppsWithPdf = useMemo(
    () =>
      registeredApps.filter(
        (a) => a.orderOfRegistrationFileKey && a.orderOfRegistrationFilename
      ),
    [registeredApps]
  );
  const refusedAppsWithPdf = useMemo(
    () => refusedApps.filter((a) => a.orderFileKey && a.orderFilename),
    [refusedApps]
  );

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div
        className="flex flex-col gap-5 w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  const statusOptions: { id: FinalOrderStatus; label: string }[] = [
    { id: "registered", label: "Registered" },
    { id: "refused-or-abandoned", label: "Refused or Abandoned" },
  ];

  const activeApps =
    finalOrderStatus === "registered" ? registeredApps : refusedApps;
  const selectedTmNo =
    finalOrderStatus === "registered"
      ? selectedRegisteredTmNo
      : selectedRefusedTmNo;
  const setSelectedTmNo =
    finalOrderStatus === "registered"
      ? setSelectedRegisteredTmNo
      : setSelectedRefusedTmNo;

  const acknowledgementNo =
    finalOrderStatus === "registered"
      ? registeredStatus?.acknowledgementNo
      : refusedStatus?.acknowledgementNo;
  const dateOfFiling =
    finalOrderStatus === "registered"
      ? registeredStatus?.dateOfFiling
      : refusedStatus?.dateOfFiling;

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Status toggle: Registered / Refused or Abandoned */}
      <div className="flex flex-row flex-wrap gap-3">
        {statusOptions.map((opt) => {
          const isActive = finalOrderStatus === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleStatusSwitch(opt.id)}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[180px] text-xs font-normal transition-colors ${
                isActive
                  ? "bg-[#222222] text-white"
                  : "bg-[#F7F7F7] text-[#222222]"
              }`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <RadioCircle active={isActive} />
              <span className="text-left line-clamp-1">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* TM number picker — populated from API for the active status */}
      {activeApps.length > 0 && (
        <div className="flex flex-row flex-wrap gap-3">
          {activeApps.map((app) => {
            const isActive = selectedTmNo === app.tmNo;
            return (
              <button
                key={app.tmNo}
                type="button"
                onClick={() => setSelectedTmNo(app.tmNo)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
                style={{ fontFamily: "Inter, sans-serif", fontSize: "0.75rem" }}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(app.tmNo)}
              </button>
            );
          })}
        </div>
      )}

      {activeApps.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#EEEEEE] rounded-xl py-12 px-6 text-center"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-sm font-medium text-[#222222]">
            No applications with this status yet.
          </p>
          <p className="text-xs text-[#6D6D6D]">
            {finalOrderStatus === "registered"
              ? "Applications will appear here once their status is set to Registered."
              : "Applications will appear here once their status is set to Refused or Abandoned."}
          </p>
        </div>
      ) : (
        <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex min-h-0">
          {/* Left: sub-step list */}
          <div className="w-[235px] flex-shrink-0 border-r border-[#EEEEEE] p-2 flex flex-col gap-1 overflow-y-auto">
            {subSteps.map((step) => {
              const isActive = subStep === step.id;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => setSubStep(step.id)}
                  className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs transition-colors ${
                    isActive
                      ? "bg-[#222222] text-white font-medium"
                      : "bg-[#F7F7F7] text-[#222222] hover:bg-[#EEEEEE]"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                  <ChevronRight active={isActive} />
                </button>
              );
            })}
          </div>

          {/* Right: detail panel */}
          <div className="flex-1 min-w-0 px-4 py-4 md:px-5 md:py-5 overflow-y-auto">
            {finalOrderStatus === "registered" ? (
              subStep === "status-details" ? (
                <form
                  id={FINAL_ORDER_REGISTERED_STATUS_FORM_ID}
                  onSubmit={(ev) => void handleRegisteredStatusSubmit(ev)}
                  className="flex flex-col gap-5"
                >
                  {selectedRegisteredApp && (
                    <RegisteredStatusFields
                      app={selectedRegisteredApp}
                      acknowledgementNo={acknowledgementNo ?? null}
                      dateOfFiling={dateOfFiling ?? null}
                      form={
                        registeredForms[selectedRegisteredApp.tmNo] ?? {
                          dateOfIssue: "",
                          certificateNo: "",
                          pendingFile: null,
                        }
                      }
                      onDateOfIssueChange={(d) =>
                        handleRegisteredDateOfIssueChange(
                          selectedRegisteredApp.tmNo,
                          d
                        )
                      }
                      onCertificateNoChange={(v) =>
                        handleRegisteredCertificateNoChange(
                          selectedRegisteredApp.tmNo,
                          v
                        )
                      }
                    />
                  )}

                  {selectedRegisteredApp && (
                    <FileUploadZone
                      inputIdPrefix="final-order-registered-file"
                      tmNo={selectedRegisteredApp.tmNo}
                      pendingFile={
                        registeredForms[selectedRegisteredApp.tmNo]?.pendingFile ??
                        null
                      }
                      serverFilename={
                        selectedRegisteredApp.orderOfRegistrationFilename
                      }
                      hasServerFile={Boolean(
                        selectedRegisteredApp.orderOfRegistrationFileKey
                      )}
                      isDragging={dragOverTmNo === selectedRegisteredApp.tmNo}
                      onDragOver={() =>
                        setDragOverTmNo(selectedRegisteredApp.tmNo)
                      }
                      onDragLeave={() => setDragOverTmNo(null)}
                      onDrop={(file) => {
                        setDragOverTmNo(null);
                        handleRegisteredFileChosen(
                          selectedRegisteredApp.tmNo,
                          file
                        );
                      }}
                      onPick={(e) =>
                        handleRegisteredFileInput(
                          selectedRegisteredApp.tmNo,
                          e
                        )
                      }
                      onPreview={() =>
                        openRegisteredFilePreview(selectedRegisteredApp)
                      }
                      onRemove={() =>
                        removeRegisteredFile(selectedRegisteredApp.tmNo)
                      }
                    />
                  )}
                </form>
              ) : subStep === "notice-to-client" ? (
                <form
                  id={FINAL_ORDER_REGISTERED_NOTICE_FORM_ID}
                  onSubmit={(ev) => void handleRegisteredNoticeSubmit(ev)}
                  className="flex flex-col gap-3"
                >
                  {registeredAppsWithPdf.length === 0 ? (
                    <p className="text-sm text-[#6D6D6D]">
                      No Order of Registration PDFs uploaded yet. Add them in
                      Status Details first.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {registeredAppsWithPdf.map((app) => (
                        <NoticePdfRow
                          key={app.tmNo}
                          label={
                            app.mark ||
                            app.orderOfRegistrationFilename ||
                            app.tmNo
                          }
                          onPreview={() => openRegisteredFilePreview(app)}
                        />
                      ))}
                    </div>
                  )}

                  {hasSentRegisteredNotice && (
                    <div className="flex flex-row items-center gap-3 w-full pt-1">
                      <button
                        type="button"
                        disabled={
                          resendingRegisteredNotice ||
                          !registeredStatus?.canForwardPdfs
                        }
                        onClick={() => void handleRegisteredNoticeResend()}
                        className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {resendingRegisteredNotice
                          ? "Resending…"
                          : "Resend Message"}
                      </button>
                    </div>
                  )}
                </form>
              ) : (
                <form
                  id={FINAL_ORDER_REGISTERED_INFORM_FORM_ID}
                  onSubmit={(ev) => void handleRegisteredInformSubmit(ev)}
                  className="flex flex-col gap-2.5"
                >
                  <MessagePreviewCard
                    loading={registeredMessageLoading}
                    message={registeredMessage}
                    phone={phone}
                  />
                  {hasSentRegisteredInform && (
                    <div className="flex flex-row items-center gap-3 w-full pt-1">
                      <button
                        type="button"
                        disabled={
                          resendingRegisteredInform || registeredMessageLoading
                        }
                        onClick={() => void handleRegisteredInformResend()}
                        className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {resendingRegisteredInform
                          ? "Resending…"
                          : "Resend Message"}
                      </button>
                    </div>
                  )}
                </form>
              )
            ) : subStep === "status-details" ? (
              <form
                id={FINAL_ORDER_REFUSED_STATUS_FORM_ID}
                onSubmit={(ev) => void handleRefusedStatusSubmit(ev)}
                className="flex flex-col gap-5"
              >
                {selectedRefusedApp && (
                  <RefusedStatusFields
                    app={selectedRefusedApp}
                    acknowledgementNo={acknowledgementNo ?? null}
                    dateOfFiling={dateOfFiling ?? null}
                    form={
                      refusedForms[selectedRefusedApp.tmNo] ?? {
                        refusalOrAbandonment: "",
                        dateOfOrder: "",
                        refusalReason: "",
                        pendingFile: null,
                      }
                    }
                    onOptionChange={(v) =>
                      handleRefusedOptionChange(selectedRefusedApp.tmNo, v)
                    }
                    onDateChange={(d) =>
                      handleRefusedDateChange(selectedRefusedApp.tmNo, d)
                    }
                    onReasonChange={(v) =>
                      handleRefusedReasonChange(selectedRefusedApp.tmNo, v)
                    }
                  />
                )}

                {selectedRefusedApp && (
                  <FileUploadZone
                    inputIdPrefix="final-order-refused-file"
                    tmNo={selectedRefusedApp.tmNo}
                    pendingFile={
                      refusedForms[selectedRefusedApp.tmNo]?.pendingFile ?? null
                    }
                    serverFilename={selectedRefusedApp.orderFilename}
                    hasServerFile={Boolean(selectedRefusedApp.orderFileKey)}
                    isDragging={dragOverTmNo === selectedRefusedApp.tmNo}
                    onDragOver={() => setDragOverTmNo(selectedRefusedApp.tmNo)}
                    onDragLeave={() => setDragOverTmNo(null)}
                    onDrop={(file) => {
                      setDragOverTmNo(null);
                      handleRefusedFileChosen(selectedRefusedApp.tmNo, file);
                    }}
                    onPick={(e) =>
                      handleRefusedFileInput(selectedRefusedApp.tmNo, e)
                    }
                    onPreview={() => openRefusedFilePreview(selectedRefusedApp)}
                    onRemove={() => removeRefusedFile(selectedRefusedApp.tmNo)}
                  />
                )}
              </form>
            ) : subStep === "notice-to-client" ? (
              <form
                id={FINAL_ORDER_REFUSED_NOTICE_FORM_ID}
                onSubmit={(ev) => void handleRefusedNoticeSubmit(ev)}
                className="flex flex-col gap-3"
              >
                {refusedAppsWithPdf.length === 0 ? (
                  <p className="text-sm text-[#6D6D6D]">
                    No Order of Refusal/Abandonment PDFs uploaded yet. Add them
                    in Status Details first.
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {refusedAppsWithPdf.map((app) => (
                      <NoticePdfRow
                        key={app.tmNo}
                        label={app.mark || app.orderFilename || app.tmNo}
                        onPreview={() => openRefusedFilePreview(app)}
                      />
                    ))}
                  </div>
                )}

                {hasSentRefusedNotice && (
                  <div className="flex flex-row items-center gap-3 w-full pt-1">
                    <button
                      type="button"
                      disabled={
                        resendingRefusedNotice || !refusedStatus?.canForwardPdfs
                      }
                      onClick={() => void handleRefusedNoticeResend()}
                      className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {resendingRefusedNotice ? "Resending…" : "Resend Message"}
                    </button>
                  </div>
                )}
              </form>
            ) : (
              <form
                id={FINAL_ORDER_REFUSED_INFORM_FORM_ID}
                onSubmit={(ev) => void handleRefusedInformSubmit(ev)}
                className="flex flex-col gap-2.5"
              >
                {!refusedStatus?.canSendMessage ? (
                  <p className="text-xs text-[#6D6D6D] leading-5">
                    Set the refusal reason for all applications in &quot;Status
                    details&quot; before sending the message.
                  </p>
                ) : refusedMessageLoading || refusedMessages.length <= 1 ? (
                  <MessagePreviewCard
                    loading={refusedMessageLoading}
                    message={refusedMessages[0]?.message ?? null}
                    phone={phone}
                  />
                ) : (
                  /* Item 89 — more than one group means the applications differ in
                     status and/or reason, so each gets its own message. Label every
                     card with what it covers so staff can see why there are several. */
                  <div className="flex flex-col gap-4">
                    <p className="text-xs text-[#6D6D6D] leading-5">
                      These applications have different statuses or reasons, so{" "}
                      {refusedMessages.length} separate messages will be sent.
                    </p>
                    {refusedMessages.map((group, idx) => (
                      <div
                        key={`${group.refusalOrAbandonment ?? "none"}-${
                          group.refusalReason ?? "none"
                        }-${idx}`}
                        className="flex flex-col gap-1.5"
                      >
                        <span
                          className="text-xs font-medium text-[#222222]"
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          Message {idx + 1} of {refusedMessages.length}
                          {group.refusalOrAbandonment
                            ? ` — ${group.refusalOrAbandonment}`
                            : ""}
                          {group.tmNos.length
                            ? ` (${group.tmNos
                                .map(formatTmNoForDisplay)
                                .join(", ")})`
                            : ""}
                        </span>
                        <MessagePreviewCard
                          loading={false}
                          message={group.message}
                          phone={phone}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {hasSentRefusedInform && (
                  <div className="flex flex-row items-center gap-3 w-full pt-1">
                    <button
                      type="button"
                      disabled={
                        resendingRefusedInform || refusedMessageLoading
                      }
                      onClick={() => void handleRefusedInformResend()}
                      className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {resendingRefusedInform ? "Resending…" : "Resend Message"}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url ?? null}
        kind={preview?.kind}
      />

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction ? CONFIRM_ACTION_COPY[confirmAction].title : ""}
        description={
          confirmAction ? CONFIRM_ACTION_COPY[confirmAction].description : ""
        }
        confirmLabel={
          confirmAction
            ? CONFIRM_ACTION_COPY[confirmAction].confirmLabel
            : "Confirm"
        }
        busy={confirmBusy}
        onClose={() => {
          if (!confirmBusy) setConfirmAction(null);
        }}
        onConfirm={() => {
          switch (confirmAction) {
            case "send-registered-notice":
              void performRegisteredNoticeSend();
              break;
            case "resend-registered-notice":
              void performRegisteredNoticeResend();
              break;
            case "send-registered-inform":
              void performRegisteredInformSend();
              break;
            case "resend-registered-inform":
              void performRegisteredInformResend();
              break;
            case "send-refused-notice":
              void performRefusedNoticeSend();
              break;
            case "resend-refused-notice":
              void performRefusedNoticeResend();
              break;
            case "send-refused-inform":
              void performRefusedInformSend();
              break;
            case "resend-refused-inform":
              void performRefusedInformResend();
              break;
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RegisteredStatusFields({
  app,
  acknowledgementNo,
  dateOfFiling,
  form,
  onDateOfIssueChange,
  onCertificateNoChange,
}: {
  app: RegistrationDetailsApplication;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  form: RegisteredFormState;
  onDateOfIssueChange: (d: Date | null) => void;
  onCertificateNoChange: (v: string) => void;
}) {
  const dateOfIssueParts = parseDateParts(form.dateOfIssue);
  const dateOfIssueValue = dateOfIssueParts ? partsToDate(dateOfIssueParts) : null;
  const validityDateDisplay = formatDisplayDate(app.validityDate);

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel>Acknowledgement No.</FieldLabel>
          <TextInput value={acknowledgementNo ?? ""} readOnly />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel>Date of Filing</FieldLabel>
          <ReadOnlyDateBox value={formatDisplayDate(dateOfFiling)} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel required>Date of issue of Registration Certificate</FieldLabel>
          <DateTimePicker
            value={dateOfIssueValue}
            onChange={(d) => onDateOfIssueChange(d ?? null)}
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel required>Certificate No. of Registration</FieldLabel>
          <TextInput
            value={form.certificateNo}
            onChange={onCertificateNoChange}
            placeholder="Certificate No. of Registration"
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel>Validity date of Registration</FieldLabel>
          <div
            className="w-full h-9 px-2.5 py-2 text-xs font-normal text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-[#FCFCFC] flex items-center"
            style={{ fontFamily: "Inter, sans-serif", lineHeight: "20px" }}
            title="Auto-calculated as Date of Filing + 10 years"
          >
            {validityDateDisplay}
          </div>
        </div>
      </div>
    </>
  );
}

function RefusedStatusFields({
  app,
  acknowledgementNo,
  dateOfFiling,
  form,
  onOptionChange,
  onDateChange,
  onReasonChange,
}: {
  app: RefusalAbandonmentDetailsApplication;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  form: RefusedFormState;
  onOptionChange: (v: RefusalOrAbandonmentOption) => void;
  onDateChange: (d: Date | null) => void;
  onReasonChange: (v: RefusalAbandonmentReasonValue) => void;
}) {
  // Silence unused-variable lint for `app` — kept for symmetry with Registered side
  void app;
  const dateOfOrderParts = parseDateParts(form.dateOfOrder);
  const dateOfOrderValue = dateOfOrderParts
    ? partsToDate(dateOfOrderParts)
    : null;

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel>Acknowledgement No.</FieldLabel>
          <TextInput value={acknowledgementNo ?? ""} readOnly />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel>Date of Filing</FieldLabel>
          <ReadOnlyDateBox value={formatDisplayDate(dateOfFiling)} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel required>Refusal or Abandonment</FieldLabel>
          <Select
            value={form.refusalOrAbandonment || undefined}
            onValueChange={(v) =>
              onOptionChange(v as RefusalOrAbandonmentOption)
            }
          >
            <SelectTrigger className="h-9 text-xs border-[#EEEEEE]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Refused">Refused</SelectItem>
              <SelectItem value="Abandoned">Abandoned</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <FieldLabel required>Date of Order of Refusal or Abandonment</FieldLabel>
          <DateTimePicker
            value={dateOfOrderValue}
            onChange={(d) => onDateChange(d ?? null)}
            placeholder="Select Date"
            maxDate={new Date()}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1 min-w-0">
        <FieldLabel required>Refusal Reason</FieldLabel>
        <Select
          value={form.refusalReason || undefined}
          onValueChange={(v) => onReasonChange(v as RefusalAbandonmentReasonValue)}
        >
          <SelectTrigger className="h-9 text-xs border-[#EEEEEE]">
            <SelectValue placeholder="Select refusal reason" />
          </SelectTrigger>
          <SelectContent>
            {REFUSAL_REASON_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

function MessagePreviewCard({
  loading,
  message,
  phone,
}: {
  loading: boolean;
  message: string | null;
  phone: string;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-sm font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>

      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden">
        <div className="px-4 py-4 min-h-[80px]">
          {loading ? (
            <span
              className="text-xs text-[#6D6D6D]"
              style={{ lineHeight: "20px" }}
            >
              Loading preview…
            </span>
          ) : message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(message)}
            </p>
          ) : (
            <span
              className="text-xs text-[#6D6D6D]"
              style={{ lineHeight: "20px" }}
            >
              No preview available
            </span>
          )}
        </div>

        <div className="border-t border-[#EEEEEE] flex items-center gap-3 px-3 py-2.5">
          <div className="flex-1 min-w-0 h-9 flex items-center px-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7]">
            <span
              className="text-xs text-[#222222] truncate"
              style={{ lineHeight: "20px" }}
            >
              {phone || "(No phone number)"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
